import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { WiredMemberAvatar } from '@euler/app/tv/components/MemberAvatar';
import { MemberTitle } from '@euler/app/tv/components/MemberTitle';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useOrgMemberDetail } from '@euler/functions/useOrgMemberDetail';
import { isNotNull } from '@euler/utils';
import { MotiView } from 'moti';
import { memo, Suspense, useMemo } from 'react';
import ContentLoader, { Rect } from 'react-content-loader/native';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import Markdown from 'react-native-markdown-display';
import Animated, {
  Extrapolate,
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';

export const MemberDetailSlide = memo(
  ({
    width,
    height,
    scrollOffset,
    index,
    memberId,
    visible,
    style,
  }: {
    width: number;
    height: number;
    scrollOffset: SharedValue<number>;
    index: number;
    visible: boolean;
    memberId: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const animatedStyle = useAnimatedStyle(() => {
      const input = scrollOffset.value / width;
      const inputRange = [index - 1, index, index + 1];

      return {
        transform: [
          {
            scale: interpolate(
              input,
              inputRange,
              [0.8, 1, 0.8],
              Extrapolate.CLAMP,
            ),
          },
        ],
      };
    });

    return (
      <Animated.View
        key={index}
        css={`
          flex: 1;
          width: ${width}px;
          height: ${height}px;
          background-color: ${theme.task.siteDetail.bgColor};
          border-radius: ${rpx(40)}px;
          overflow: hidden;
        `}
        style={[style, animatedStyle]}
      >
        {visible ? (
          <Suspense
            fallback={
              <Center
                css={`
                  flex: 1;
                `}
              >
                <MemberDetailSlideLoader width={width} height={height} />
              </Center>
            }
          >
            <Content memberId={memberId} />
          </Suspense>
        ) : null}
      </Animated.View>
    );
  },
);

const Content = memo(({ memberId }: { memberId: number }) => {
  const member = useOrgMemberDetail(memberId);
  const { rpx } = useResponsiveContext();
  const {
    emptyPlaceholder,
    memberInfo: {
      primaryBgColor,
      secondaryBgColor,
      nameTextColor,
      jobTitleTextColor,
      detail: { bodyTextColor, quoteBgColor, quoteBorderColor },
    },
  } = usePresentationTheme();

  const description = [
    member?.honourTitle ? `**荣誉头衔**: ${member.honourTitle}` : undefined,
    member?.academicTitle ? `**技能职称**: ${member.academicTitle}` : undefined,
    member?.specialities ? `**技能专长**: ${member.specialities}` : undefined,
    member?.remark,
    member?.description,
  ].filter(isNotNull);

  const styles = useMemo<StyleSheet.NamedStyles<any>>(
    () => ({
      body: {
        fontSize: rpx(28),
        color: bodyTextColor,
      },
      blockquote: {
        borderLeftColor: quoteBorderColor,
        borderColor: quoteBorderColor,
        backgroundColor: quoteBgColor,
      },
      image: {
        borderRadius: rpx(20),
        overflow: 'hidden',
      },
    }),
    [bodyTextColor, quoteBgColor, quoteBorderColor, rpx],
  );

  if (member == null) {
    return (
      <EmptyPlaceholder
        text="未找到该员工的信息"
        iconSize={rpx(100)}
        textStyle={{
          fontSize: rpx(36),
        }}
      />
    );
  }

  return (
    <MotiView
      css={`
        flex: 1;
        background-color: ${primaryBgColor};
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
      `}
    >
      <View
        css={`
          width: 35%;
          background-color: ${secondaryBgColor};
          justify-content: center;
        `}
      >
        <View
          css={`
            align-items: center;
          `}
        >
          <WiredMemberAvatar
            memberId={memberId}
            size={rpx(235)}
            css={`
              border-radius: ${rpx(12)}px;
            `}
          />
          <Typography.Label
            bold
            size={rpx(56)}
            color={nameTextColor}
            lineHeight={rpx(80)}
            css={`
              margin-top: ${rpx(20)}px;
            `}
          >
            {member.name}
          </Typography.Label>
          <MemberTitle
            role={member.role}
            jobTitle={member.jobTitle}
            css={`
              font-size: ${rpx(24)}px;
              line-height: ${rpx(36)}px;
              color: ${jobTitleTextColor};
            `}
          />
        </View>
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          padding: rpx(40),
        }}
      >
        {description.length > 0 ? (
          <Markdown style={styles}>{description.join('\n\n')}</Markdown>
        ) : (
          <EmptyPlaceholder
            icon={
              <Icon
                color={emptyPlaceholder.iconColor}
                name="file-tray-outline"
                size={rpx(150)}
              />
            }
            text="暂无员工介绍"
            css={`
              flex: 1;
            `}
          />
        )}
      </ScrollView>
    </MotiView>
  );
});

export const MemberDetailSlideLoader = memo(
  ({
    width,
    height,
    style,
  }: {
    width: number;
    height: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <Center
        css={`
          flex: 1;
        `}
      >
        <Center
          css={`
            width: ${width}px;
            height: ${height}px;
            border-radius: ${rpx(40)}px;
            background-color: ${theme.memberInfo.primaryBgColor};
            padding-left: ${width / 3}px;
            padding-top: ${rpx(40)}px;
          `}
        >
          <ContentLoader
            key={width}
            viewBox="0 0 1300 500"
            speed={2}
            width={width}
            height={width * (5 / 13)}
            backgroundColor={theme.contentLoader.bg}
            foregroundColor={theme.contentLoader.fg}
            style={style}
          >
            <Rect x="20" y="15" rx="20" ry="20" width="300" height="320" />
            <Rect x="92" y="347" rx="5" ry="5" width="45" height="45" />
            <Rect x="148" y="347" rx="5" ry="5" width="45" height="45" />
            <Rect x="205" y="347" rx="5" ry="5" width="45" height="45" />
            <Rect x="361" y="17" rx="10" ry="10" width="420" height="33" />
            <Rect x="361" y="71" rx="10" ry="10" width="315" height="33" />
            <Rect x="361" y="125" rx="10" ry="10" width="233" height="20" />
            <Rect x="361" y="216" rx="5" ry="5" width="195" height="13" />
            <Rect x="361" y="251" rx="5" ry="5" width="195" height="13" />
            <Rect x="367" y="311" rx="8" ry="8" width="130" height="38" />
            <Rect x="515" y="311" rx="8" ry="8" width="130" height="38" />
          </ContentLoader>
        </Center>
      </Center>
    );
  },
);
